import React, { useState } from 'react';
import { ChevronRightIcon, ClockIcon, FolderIcon, FolderOpenIcon, HardDriveIcon, StarIcon, Trash2Icon } from 'lucide-react';
import { childrenOf, isFolder } from '../../utils/fileTree';
import { formatSize } from '../../utils/format';
import { STORAGE_QUOTA_KB, type FsNode, type ViewId } from '../../types/files';
interface SidebarProps {
  nodes: FsNode[];
  view: ViewId;
  folderId: string | null;
  counts: {
    all: number;
    folders: number;
    starred: number;
    trash: number;
    recent: number;
  };
  usedKb: number;
  onChangeView: (view: ViewId) => void;
  onGoToFolder: (id: string | null) => void;
  onDropOnFolder: (targetId: string | null) => void;
  draggingIds: string[];
}
export function Sidebar({
  nodes,
  view,
  folderId,
  counts,
  usedKb,
  onChangeView,
  onGoToFolder,
  onDropOnFolder,
  draggingIds
}: SidebarProps) {
  const usedPercent = Math.min(100, usedKb / STORAGE_QUOTA_KB * 100);
  const views: {
    id: ViewId;
    label: string;
    icon: typeof FolderIcon;
    count: number;
  }[] = [{
    id: 'files',
    label: 'All Files',
    icon: FolderOpenIcon,
    count: counts.all
  }, {
    id: 'recent',
    label: 'Recent',
    icon: ClockIcon,
    count: counts.recent
  }, {
    id: 'starred',
    label: 'Starred',
    icon: StarIcon,
    count: counts.starred
  }, {
    id: 'trash',
    label: 'Recycle Bin',
    icon: Trash2Icon,
    count: counts.trash
  }];
  return <nav aria-label="File library" className="flex w-full shrink-0 flex-col gap-5 border-b border-slate-200 bg-white p-4 lg:h-full lg:w-64 lg:border-b-0 lg:border-r" data-id="element-319" data-mp="e98v8y00x6y7">
      <ul className="space-y-0.5" data-id="element-320" data-mp="nxqv9d0hba0s">
        {views.map(item => {
        const Icon = item.icon;
        const isActive = view === item.id;
        return <li key={item.id} data-id="element-321" data-mp="sz6dyyf6p4g0">
              <button type="button" aria-current={isActive ? 'page' : undefined} onClick={() => onChangeView(item.id)} className={['flex w-full items-center gap-2 rounded px-2.5 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]', isActive ? 'bg-[#eef4ff] text-[#014486]' : 'text-slate-700 hover:bg-slate-100'].join(' ')} data-id="element-322" data-mp="wzguv64ilf9n">
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-323" data-mp="32y6ig3a0i1li" />
                <span className="flex-1 text-left" data-id="element-324" data-mp="3hnuk55m3v29r">{item.label}</span>
                <span className="tabular-nums text-xs text-slate-500" data-id="element-325" data-mp="3cqm1z50yiy8i">
                  {item.count}
                </span>
              </button>
            </li>;
      })}
      </ul>

      <div data-id="element-326" data-mp="3lq93dqtos41k">
        <p className="px-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-327" data-mp="3lqiwhna12rbh">
          Folders
        </p>
        <div className="mt-1.5" data-id="element-328" data-mp="1wcm4e0pl3kof">
          <FolderButton label="Loan Documents" depth={0} isActive={view === 'files' && folderId === null} hasChildren={false} isOpen onToggle={() => undefined} onSelect={() => onGoToFolder(null)} onDrop={() => onDropOnFolder(null)} canDrop={draggingIds.length > 0} root data-id="element-329" data-mp="1wdnqr1c1vtlg" />
          <FolderTree nodes={nodes} parentId={null} depth={1} activeId={view === 'files' ? folderId : null} onGoToFolder={onGoToFolder} onDropOnFolder={onDropOnFolder} draggingIds={draggingIds} data-id="element-330" data-mp="3u5ylaqak3zv2" />
        </div>
      </div>

      <div className="mt-auto rounded border border-slate-200 bg-slate-50 p-3" data-id="element-331" data-mp="3b96uzwwlkkhi">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700" data-id="element-332" data-mp="3bb9ud7jn3pyw">
          <HardDriveIcon className="h-4 w-4" aria-hidden="true" data-id="element-333" data-mp="3fclcyt43jaxl" />
          Storage
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200" role="progressbar" aria-valuenow={Math.round(usedPercent)} aria-valuemin={0} aria-valuemax={100} aria-label="Storage used" data-id="element-334" data-mp="19ffyxa32lkj6">
          <div className="h-full rounded-full bg-[#0176d3]" style={{
          width: `${Math.max(2, usedPercent)}%`
        }} data-id="element-335" data-mp="jty427wqvli4" />
        </div>
        <p className="mt-1.5 text-xs text-slate-600" data-id="element-336" data-mp="9vqpfvpxhfja">
          {formatSize(usedKb)} of 15 GB used
        </p>
      </div>
    </nav>;
}
interface FolderTreeProps {
  nodes: FsNode[];
  parentId: string | null;
  depth: number;
  activeId: string | null;
  onGoToFolder: (id: string | null) => void;
  onDropOnFolder: (targetId: string | null) => void;
  draggingIds: string[];
}
function FolderTree({
  nodes,
  parentId,
  depth,
  activeId,
  onGoToFolder,
  onDropOnFolder,
  draggingIds
}: FolderTreeProps) {
  const folders = childrenOf(nodes, parentId).filter(isFolder);
  const [openIds, setOpenIds] = useState<string[]>([]);
  if (folders.length === 0) return null;
  return <ul data-id="element-337" data-mp="1troel9x6daus">
      {folders.map(folder => {
      const hasChildren = childrenOf(nodes, folder.id).some(isFolder);
      const isOpen = openIds.includes(folder.id);
      return <li key={folder.id} data-id="element-338" data-mp="22s32mh1ix41z">
            <FolderButton label={folder.name} depth={depth} isActive={activeId === folder.id} hasChildren={hasChildren} isOpen={isOpen} onToggle={() => setOpenIds(current => current.includes(folder.id) ? current.filter(id => id !== folder.id) : [...current, folder.id])} onSelect={() => onGoToFolder(folder.id)} onDrop={() => onDropOnFolder(folder.id)} canDrop={draggingIds.length > 0 && !draggingIds.includes(folder.id)} data-id="element-339" data-mp="22rtc8sckguvr" />
            {isOpen && <FolderTree nodes={nodes} parentId={folder.id} depth={depth + 1} activeId={activeId} onGoToFolder={onGoToFolder} onDropOnFolder={onDropOnFolder} draggingIds={draggingIds} data-id="element-340" data-mp="wafufyenxi6u" />}
          </li>;
    })}
    </ul>;
}
interface FolderButtonProps {
  label: string;
  depth: number;
  isActive: boolean;
  hasChildren: boolean;
  isOpen: boolean;
  canDrop: boolean;
  root?: boolean;
  onToggle: () => void;
  onSelect: () => void;
  onDrop: () => void;
}
function FolderButton({
  label,
  depth,
  isActive,
  hasChildren,
  isOpen,
  canDrop,
  root = false,
  onToggle,
  onSelect,
  onDrop
}: FolderButtonProps) {
  const [isOver, setIsOver] = useState(false);
  return <div onDragOver={event => {
    if (!canDrop) return;
    event.preventDefault();
    setIsOver(true);
  }} onDragLeave={() => setIsOver(false)} onDrop={event => {
    if (!canDrop) return;
    event.preventDefault();
    setIsOver(false);
    onDrop();
  }} style={{
    paddingLeft: `${depth * 12}px`
  }} className={['flex items-center gap-0.5 rounded', isOver ? 'ring-2 ring-[#0176d3] ring-offset-1' : ''].join(' ')} data-id="element-341" data-mp="1qptbnxv6dzgw">
      {hasChildren ? <button type="button" onClick={onToggle} aria-label={isOpen ? `Collapse ${label}` : `Expand ${label}`} aria-expanded={isOpen} className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-342" data-mp="2znjevxnkpij4">
          <ChevronRightIcon className={['h-3.5 w-3.5 transition', isOpen ? 'rotate-90' : ''].join(' ')} aria-hidden="true" data-id="element-343" data-mp="3il2uxl8g81f1" />
        </button> : <span className="h-5 w-5 shrink-0" data-id="element-344" data-mp="exkkys3d59zi" />}
      <button type="button" onClick={onSelect} aria-current={isActive ? 'true' : undefined} className={['flex min-w-0 flex-1 items-center gap-2 rounded px-1.5 py-1.5 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]', isActive ? 'bg-[#eef4ff] font-semibold text-[#014486]' : 'text-slate-700 hover:bg-slate-100', root ? 'font-medium' : ''].join(' ')} data-id="element-345" data-mp="eyc433frxf8w">
        <FolderIcon className="h-4 w-4 shrink-0 text-[#0176d3]" aria-hidden="true" data-id="element-346" data-mp="2tw5qccwx8pkd" />
        <span className="truncate" data-id="element-347" data-mp="2k0c2hxdr59na">{label}</span>
      </button>
    </div>;
}